import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTheme } from '../contexts/ThemeContext';
import { useSEO } from '../contexts/SEOContext';
import { useLocation } from 'react-router-dom';

/**
 * SEO Head Component
 * 
 * Dynamically updates meta tags based on current page and domain configuration.
 * Uses seo.pages from domain config for page-specific titles and descriptions.
 * 
 * Also handles:
 * - Canonical URLs - uses actual domain from window.location.origin for proper multi-domain support
 * - Structured Data (JSON-LD) - dynamically generated based on current page
 * - MS Application tiles
 * - Mobile app meta tags
 * - Preconnect hints
 * 
 * Note on Canonical Tags & SEO:
 * Google's crawler (Googlebot) renders JavaScript and can see client-side canonical tags.
 * We use window.location.origin to get the actual domain being accessed, ensuring
 * the canonical URL is correct regardless of which domain serves the content.
 */
const SEOHead = ({ structuredData }) => {
  const { domainConfig } = useTheme();
  const { currentSEO, generateStructuredData, generateBreadcrumbStructuredData } = useSEO();
  const location = useLocation();
  
  const domain = domainConfig;
  
  // State for canonical URL - only set on client-side from actual browser location
  // We start with null to indicate "not yet determined" - no canonical tag will be rendered during pre-render
  const [canonicalOrigin, setCanonicalOrigin] = useState(null);
  
  // Effect to set canonical origin from actual browser location
  // This runs on client-side and sets the canonical to reflect the real domain
  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Get the actual origin (e.g., https://www.geotabcamera.com or https://geotab.cam)
      const origin = window.location.origin;
      setCanonicalOrigin(origin);
    }
  }, []);
  
  // Use structured data from SEO context or generate custom
  const structuredDataToUse = structuredData || generateStructuredData();
  const breadcrumbData = generateBreadcrumbStructuredData();

  // Get page-specific or fallback SEO data
  // og:title, og:description, twitter:title, twitter:description match the page title/description
  const pageTitle = currentSEO?.title || domain?.seo?.title || domain?.name || 'Fleet Management Solutions';
  const pageDescription = currentSEO?.description || domain?.seo?.description || domain?.description || 'Advanced fleet management solutions';
  const pageKeywords = currentSEO?.keywords || domain?.seo?.keywords || [];
  
  // Build URLs
  // og:url uses config's ogUrl (primary domain) - this is fine for social sharing
  // Canonical URL uses actual window.location.origin (set client-side only)
  const configBaseUrl = domain?.seo?.ogUrl || '';
  const pageUrl = `${configBaseUrl}${location.pathname}`;
  
  // Canonical URL - only available after client-side hydration
  const canonicalUrl = canonicalOrigin 
    ? `${canonicalOrigin}${location.pathname}`.replace(/\/$/, '') || canonicalOrigin
    : null;
  
  // Build image URLs - always use logo for og:image and twitter:image
  const logoPath = domain?.logo ? `${domain.logo}.png` : '/assets/logos/default-logo.png';
  const ogImage = `${configBaseUrl}${logoPath}`;
  const twitterImage = ogImage;

  return (
    <Helmet key={`${domain?.id}-${location.pathname}-${canonicalOrigin || 'prerender'}`}>
      {/* Dynamic Title and Description - updates based on current page */}
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      {pageKeywords.length > 0 && (
        <meta name="keywords" content={pageKeywords.join(', ')} />
      )}
      
      {/* Canonical URL - only rendered client-side using actual window.location.origin */}
      {/* This ensures the canonical reflects the real domain being accessed */}
      {/* Google's crawler renders JavaScript, so this client-side canonical is properly indexed */}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      
      {/* Open Graph tags - all required tags */}
      <meta property="og:type" content={domain?.seo?.ogType || 'website'} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:site_name" content={domain?.name || 'Fleet Management'} />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter Card tags - all required tags */}
      <meta name="twitter:card" content={domain?.seo?.twitterCard || 'summary_large_image'} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={twitterImage} />
      
      {/* Structured Data - dynamically generated based on current page */}
      <script type="application/ld+json">
        {JSON.stringify(structuredDataToUse)}
      </script>
      
      {/* Breadcrumb Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbData)}
      </script>
      
      {/* MS Application Tile - not in build script */}
      <meta name="msapplication-TileColor" content="#452F80" />
      <meta name="msapplication-TileImage" content={domain?.icon || '/assets/icons/ms-tile-icon.png'} />
      
      {/* Mobile App Meta Tags - not in build script */}
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content={domain?.name} />
      
      {/* Preconnect to external domains for performance */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link rel="preconnect" href="https://www.google-analytics.com" />
      <link rel="preconnect" href="https://www.googletagmanager.com" />
    </Helmet>
  );
};

export default SEOHead;
